import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Linkedin, Mail, MessageSquare, FileText, Loader2, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useSettings } from "@/providers/SettingsProvider";
import { format, subMonths, startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";

export const IntegrationsTab = () => {
	const [integrations, setIntegrations] = useState({
		linkedin: false,
		google: false,
		microsoft: false,
		whatsapp: false
	});
	const [loading, setLoading] = useState(true);
	const [processingProvider, setProcessingProvider] = useState<string | null>(null);
	const [disconnectDialog, setDisconnectDialog] = useState<{
		open: boolean;
		provider: string;
	}>({ open: false, provider: "" });
	const [searchParams] = useSearchParams();
	const processedCodeRef = useRef<string | null>(null);
	const { saveSettings, hasUnsavedChanges, setHasUnsavedChanges } = useSettings();
	const { t } = useTranslation();
	
	// Get default date (start of current month)
	const defaultSyncDate = startOfMonth(new Date());
	
	// Define the minimum allowed date (6 months ago)
	const minSyncDate = subMonths(new Date(), 6);
	
	// Define sync initial date for Google and Microsoft
	const [syncDates, setSyncDates] = useState({
		google: defaultSyncDate,
		microsoft: defaultSyncDate
	});
	
	// Track if sync dates are already in settings
	const [syncDatesInSettings, setSyncDatesInSettings] = useState({
		google: false,
		microsoft: false
	});
	
	// Fetch existing integrations on component mount and when code param changes
	useEffect(() => {
		fetchUserIntegrations();
		fetchSyncDatesFromSettings();
		
		// Handle redirect with code parameter
		const code = searchParams.get('code');
		if (code && code !== processedCodeRef.current) {
			processedCodeRef.current = code;
			handleAuthCallback(code);
		}
	}, [searchParams]);

	const isLocalhost = () => {
		return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	};
	
	const fetchUserIntegrations = async () => {
		try {
			setLoading(true);
			// Get current user
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUser = sessionData.session?.user;

			if (!currentUser) {
				console.log("No authenticated user found");
				return;
			}

			// Query nylas_grants table for the current user
			const { data: nylasGrants, error } = await supabase
				.from('nylas_grants')
				.select('*')
				.eq('user_uuid', currentUser.id);

			if (error) {
				console.error("Error fetching integrations:", error);
				throw error;
			}

			const newIntegrationsState = { ...integrations };

			// Update state based on the query results
			if (nylasGrants) {
				nylasGrants.forEach(grant => {
					if (grant.provider === "google") {
						newIntegrationsState.google = true;
					} else if (grant.provider === "microsoft") {
						newIntegrationsState.microsoft = true;
					}
				});
			}

			setIntegrations(newIntegrationsState);
		} catch (error) {
			console.error("Failed to fetch integrations:", error);
			toast({
				title: "Error",
				description: "Failed to load integration status",
				variant: "destructive"
			});
		} finally {
			setLoading(false);
		}
	};

	// Define Settings type
	interface Settings {
		sync_init_google?: string;
		sync_init_microsoft?: string;
		[key: string]: any; // To allow other properties
	}

	// Fetch sync dates from user settings
	const fetchSyncDatesFromSettings = async () => {
		try {
			// Get current user
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUser = sessionData.session?.user;

			if (!currentUser) {
				console.log("No authenticated user found");
				return;
			}

			// Get user profile with settings
			const { data: userProfile, error } = await supabase
				.from('profiles')
				.select('settings')
				.eq('id', currentUser.id)
			
			if (userProfile && userProfile[0]?.settings) {
				const settings = userProfile[0].settings as Settings;
			
				// Check if sync dates exist in settings
				const newSyncDates = { ...syncDates };
				const newSyncDatesInSettings = { ...syncDatesInSettings };
			
				if (settings.sync_init_google) {
					newSyncDates.google = new Date(settings.sync_init_google);
					newSyncDatesInSettings.google = true;
				}
			
				if (settings.sync_init_microsoft) {
					newSyncDates.microsoft = new Date(settings.sync_init_microsoft);
					newSyncDatesInSettings.microsoft = true;
				}
			
				setSyncDates(newSyncDates);
				setSyncDatesInSettings(newSyncDatesInSettings);
			}
		} catch (error) {
			console.error("Failed to fetch sync dates from settings:", error);
		}
	};

	const handleAuthCallback = async (code: string) => {
		try {
			setProcessingProvider("processing"); // Show loading state
			
			// Get current user
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUser = sessionData.session?.user;
			
			if (!currentUser) {
				throw new Error("No authenticated user found");
			}
			
			// Call the edge function to register the grant
			const { data, error } = await supabase.functions.invoke('nylas_register_grant', {
				body: {
					codeFromAuthRequest: code,
					localDev: isLocalhost() ? true : false,
					userId: currentUser.id
				}
			});
			
			if (error) {
				throw error;
			}
			
			// Refresh the page to remove the code from URL
			window.location.href = window.location.pathname + "?origin=integrations";
			
		} catch (error) {
			console.error("Failed to register grant:", error);
			toast({
				title: "Connection Failed",
				description: "Failed to complete the authentication process. Please try again.",
				variant: "destructive"
			});
		} finally {
			setProcessingProvider(null);
		}
	};

	const handleToggle = (integration: keyof typeof integrations) => {
		// Don't allow toggling disabled integrations
		if (integration === "linkedin" || integration === "whatsapp") {
			return;
		}

		// If turning off an integration, show confirmation dialog
		if (integrations[integration]) {
			const provider = integration === "google" ? "Google" : "Office 365";
			setDisconnectDialog({ open: true, provider });
			return;
		}

		// Otherwise proceed with enabling
		setIntegrations(prev => {
			const newState = { ...prev, [integration]: !prev[integration] };

			// Show toast message
			if (newState[integration]) {
				toast({
					title: "Integration Enabled",
					description: `${integration.charAt(0).toUpperCase() + integration.slice(1)} integration has been enabled.`,
				});
			}

			return newState;
		});
	};

	const handleDisconnectConfirm = async () => {
		const integration = disconnectDialog.provider === "Google" ? "google" : "microsoft";

		try {
			// Get current user
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUser = sessionData.session?.user;

			if (!currentUser) {
				console.log("No authenticated user found");
				return;
			}

			// Delete the integration from nylas_grants table
			const { error } = await supabase
				.from('nylas_grants')
				.delete()
				.eq('user_uuid', currentUser.id)
				.eq('provider', integration === "google" ? "google" : "microsoft");

			if (error) {
				throw error;
			}

			// Update local state
			setIntegrations(prev => ({
				...prev,
				[integration]: false
			}));

			toast({
				title: "Integration Disabled",
				description: `${disconnectDialog.provider} integration has been disabled.`,
			});
		} catch (error) {
			console.error(`Error disconnecting ${disconnectDialog.provider}:`, error);
			toast({
				title: "Error",
				description: `Failed to disconnect ${disconnectDialog.provider}`,
				variant: "destructive"
			});
		} finally {
			setDisconnectDialog({ open: false, provider: "" });
		}
	};

	const handleConnect = async (integration: string) => {
		// Don't allow connecting to disabled integrations
		if (integration === "LinkedIn" || integration === "WhatsApp") {
			return;
		}
		
		const provider = integration === "google" ? "google" : "microsoft";
		
		try {
			setProcessingProvider(provider);
			
			toast({
				title: t("settings.integrations.connectionInitiated"),
				description: t("settings.integrations.connecting", { provider: integration }),
			});
			
			// Call the Supabase edge function to start the auth process
			const { data, error } = await supabase.functions.invoke('nylas_request_auth', {
				body: {
					provider: provider,
					localDev: isLocalhost() ? true : false
				}
			});
			
			if (error) {
				throw error;
			}
			
			// Redirect the user to the authentication URL
			if (data && data.authUrl) {
				window.location.href = data.authUrl;
			} else {
				throw new Error("No authentication URL received");
			}
			
		} catch (error) {
			console.error(`Error connecting to ${integration}:`, error);
			toast({
				title: t("settings.integrations.connectionError"),
				description: t("settings.integrations.connectionFailed", { provider: integration }),
				variant: "destructive"
			});
			setProcessingProvider(null);
		}
	};

	// Handle sync date change
	const handleSyncDateChange = async (provider: 'google' | 'microsoft', dateString: string) => {
		try {
			if (syncDatesInSettings[provider]) {
				// Don't allow changes if date is already in settings
				return;
			}
			
			const newDate = new Date(dateString);
			
			// Validate date is not earlier than 6 months ago
			if (newDate < minSyncDate) {
				toast({
					title: "Invalid Date",
					description: "Sync date cannot be earlier than 6 months ago",
					variant: "destructive"
				});
				return;
			}
			
			// Update local state
			setSyncDates(prev => ({
				...prev,
				[provider]: newDate
			}));
			
			// Get current user
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUser = sessionData.session?.user;
			
			if (!currentUser) {
				console.log("No authenticated user found");
				return;
			}
			
			// Get current user settings
			const { data: userProfile, error } = await supabase
				.from('profiles')
				.select('settings')
				.eq('id', currentUser.id)
				.single();
				
			if (error) {
				console.error("Error fetching user profile:", error);
				return;
			}
			
			// Prepare updated settings
			let updatedSettings = userProfile?.settings || {};
			updatedSettings[`sync_init_${provider}`] = newDate.toISOString();
			
			// Update settings in the database as a temporary storage
			// The actual save will happen when the user clicks "Save Settings"
			await supabase
				.from('profiles')
				.update({ 
					settings: updatedSettings 
				})
				.eq('id', currentUser.id);
			
			// Important: Set hasUnsavedChanges to true to trigger the Save Settings button
			setHasUnsavedChanges(true);
			
			toast({
				title: "Sync Date Updated",
				description: `${provider.charAt(0).toUpperCase() + provider.slice(1)} sync date will be saved when you click "Save Settings".`,
			});
			
		} catch (error) {
			console.error(`Error updating sync date for ${provider}:`, error);
			toast({
				title: "Error",
				description: `Failed to update sync date for ${provider}`,
				variant: "destructive"
			});
		}
	};

	// Helper function to render connection controls
	const renderConnectionControls = (provider: string, isConnected: boolean) => {
		
		const providerKey = provider === "Google" ? "google" : "microsoft";
		
		if (processingProvider === "processing" || processingProvider === providerKey) {
			return (
				<div className="flex items-center space-x-2">
					<Loader2 className="h-4 w-4 animate-spin" />
					<span className="text-sm">{t("settings.integrations.connectionProcessing")}</span>
				</div>
			);
		}
		
		return (
			<div className="flex items-center space-x-3">
				{isConnected ? (
					<Switch
						checked={isConnected}
						onCheckedChange={() => handleToggle(provider === "Google" ? "google" : "microsoft")}
						disabled={loading}
					/>
				) : (
					<Button
						variant="outline"
						size="sm"
						onClick={() => handleConnect(providerKey)}
					>
						{t("settings.integrations.connect")}
					</Button>
				)}
			</div>
		);
	};

	// Helper function to render sync date input
	const renderSyncDateInput = (provider: 'google' | 'microsoft') => {
		if (!integrations[provider]) return null;
		
		return (
			<div className="mt-4 pl-14">
				<div className="flex items-center space-x-2">
					<Calendar className="h-4 w-4 text-gray-500" />
					<span className="text-sm font-medium">{t("settings.integrations.syncDate.title")}</span>
				</div>
				<div className="mt-1">
					<Input
						type="date"
						value={syncDates[provider].toISOString().substring(0, 10)}
						onChange={(e) => handleSyncDateChange(provider, e.target.value)}
						disabled={syncDatesInSettings[provider]}
						min={minSyncDate.toISOString().substring(0, 10)}
						max={new Date().toISOString().substring(0, 10)}
						className={syncDatesInSettings[provider] ? "bg-gray-100" : ""}
					/>
					<p className="text-xs text-gray-500 mt-1">
						{syncDatesInSettings[provider]
							? t("settings.integrations.syncDate.fixedDescription", { date: format(syncDates[provider], "MMMM d, yyyy") })
							: t("settings.integrations.syncDate.description", { date: format(minSyncDate, "MMMM d, yyyy") })
						}
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{t("settings.integrations.socialTitle")}</CardTitle>
					<CardDescription>
						{t("settings.integrations.socialDescription")}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-white shadow-sm opacity-60">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-400">
								<Linkedin size={20} />
							</div>
							<div>
								<div className="flex items-center gap-2">
									<h3 className="font-medium">{t("settings.integrations.linkedin")}</h3>
									<Badge variant="outline" className="text-gray-500 bg-gray-100">{t("settings.integrations.notAvailable")}</Badge>
								</div>
								<p className="text-sm text-gray-500">{t("settings.integrations.linkedinDesc")}</p>
							</div>
						</div>
						<div className="flex items-center space-x-3">
							<Switch
								checked={false}
								disabled={true}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t("settings.integrations.businessTitle")}</CardTitle>
					<CardDescription>
						{t("settings.integrations.businessDescription")}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Nylas logo */}
					<div className="flex items-center mb-4">
						<img 
							src="/Nylas-Logo_Horizontal-Blue.png" 
							alt="Nylas Logo" 
							className="h-8 object-contain" 
						/>
					</div>

					<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow transition-shadow">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600">
								<Mail size={20} />
							</div>
							<div>
								<h3 className="font-medium">{t("settings.integrations.google")}</h3>
								<p className="text-sm text-gray-500">{t("settings.integrations.googleDesc")}</p>
							</div>
						</div>
						{renderConnectionControls("Google", integrations.google)}
					</div>
					
					{/* Google sync date input */}
					{renderSyncDateInput('google')}

					<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow transition-shadow">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
								<Mail size={20} />
							</div>
							<div>
								<h3 className="font-medium">{t("settings.integrations.microsoft")}</h3>
								<p className="text-sm text-gray-500">{t("settings.integrations.microsoftDesc")}</p>
							</div>
						</div>
						{renderConnectionControls("Microsoft", integrations.microsoft)}
					</div>
					
					{/* Microsoft sync date input */}
					{renderSyncDateInput('microsoft')}

					<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-white shadow-sm opacity-60">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-400">
								<MessageSquare size={20} />
							</div>
							<div>
								<div className="flex items-center gap-2">
									<h3 className="font-medium">{t("settings.integrations.whatsapp")}</h3>
									<Badge variant="outline" className="text-gray-500 bg-gray-100">{t("settings.integrations.notAvailable")}</Badge>
								</div>
								<p className="text-sm text-gray-500">{t("settings.integrations.whatsappDesc")}</p>
							</div>
						</div>
						<div className="flex items-center space-x-3">
							<Switch
								checked={false}
								disabled={true}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Disconnect confirmation dialog */}
			<AlertDialog
				open={disconnectDialog.open}
				onOpenChange={(open) => !open && setDisconnectDialog({ open, provider: "" })}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("settings.integrations.disconnect.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("settings.integrations.disconnect.description", { provider: disconnectDialog.provider })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("settings.integrations.disconnect.cancel")}</AlertDialogCancel>
						<AlertDialogAction onClick={handleDisconnectConfirm}>{t("settings.integrations.disconnect.confirm")}</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
